<template>
  <div>
    <div v-if="!isReadOnly" class="d-flex justify-content-end mb-1 toolbar-gap">
      <div class="col-2 px-0">
        <b-form-input
          v-model="search"
          placeholder="Buscar por SOEID, nombre"
          class="search-field"
        />
      </div>
      <div v-if="showOptionsByStatus" class="col-auto px-0">
        <b-button
          variant="outline-primary"
          @click="showModal = true"
        >
          Agregar empleado
        </b-button>
      </div>
    </div>
    <div ref="tableWrapper">
      <b-table
        ref="refListTable"
        :items="fetchList"
        :fields="employeesColumns"
        responsive
        show-empty
        empty-text="No se encontraron empleados"
      >
        <!-- Unit -->
        <template #cell(unit)="data">
          {{ data.item.unit ? data.item.unit.description : branch.description }}
        </template>

        <!-- Actions -->
        <template #cell(belongs_to_crisis)="data">
          <feather-icon
            v-if="belongsToCrisis(data.item)"
            class="text-success"
            icon="CheckCircleIcon"
            size="14"
          />
          <feather-icon
            v-else
            class="text-danger"
            icon="XCircleIcon"
            size="14"
          />
        </template>

        <template #cell(actions)="data">
          <div class="d-flex">
            <b-button
              v-if="!isReadOnly"
              variant="flat-primary"
              size="sm"
              class="p-50"
              @click.stop.prevent="selectEmployee(data.item)"
            >
              <feather-icon icon="Edit2Icon" size="14" />
            </b-button>
            <b-button
              v-if="showOptionsByStatus && !isReadOnly"
              variant="flat-secondary"
              size="sm"
              class="p-50"
              @click="$emit('delete-employee', data.item, 'users-rh')"
            >
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
          </div>
        </template>
      </b-table>
    </div>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted text-capitalize">{{
            $t("table.showing", {
              from: dataMeta.from,
              to: dataMeta.to,
              of: dataMeta.of,
            })
          }}</span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <Pagination
            data-cy="pagination"
            :totalList="totalList"
            :currentPage="currentPage"
            @updatePagination="update"
            :perPage="perPage"
          >
          </Pagination>
        </b-col>
      </b-row>
    </div>
    <users-rh-additional-modal
      :branch="branch"
      :operational-security-crisis="operationalSecurityCrisis"
      :show-modal="showModal"
      @save-employee-additional-success="handleSaveAdditionalEmployee"
      @update:isActive="showModal = false"
    />
  </div>
</template>

<script>
import { computed,ref,watch } from '@vue/composition-api';
import axios from "@/libs/axios";
import { debounce } from 'lodash';
import Pagination from "@/components/Pagination.vue";
import UsersRhAdditionalModal from './UsersRhAdditionalModal.vue';

export default {
  props:{
    branch: {
      type: Object
    },
    operationalSecurityCrisis: {
      type: Object
    },
    showOptionsByStatus:{
      type: Boolean
    },
    isFormDisabled:{
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isActive: {
      type: Boolean,
      default: true
    },
  },
  components: {
    Pagination,
    UsersRhAdditionalModal
  },
  setup (props, { emit }) {

    const tableWrapper = ref(null);

    //DATA
    const currentPage = ref(1);
    const isExternalUser = (value, key, item) => {
      const thisFacilityId = props.branch.id_unit;
      const userFacilityId = item.user_rh.id_unit;

      return thisFacilityId !== userFacilityId ? 'Sí' : 'No';
    };
    const resolveEmployeeFacility = (value, key, item) => {
      return item.user_rh.unit.description;
    };
    const employeesColumns = [
      { key: 'user_rh.soeid', label: 'SOEID'},
      { key: 'user_rh.name', label: 'Nombre' },
      { key: 'user_rh.first_name', label: 'Apellido paterno', },
      { key: 'user_rh.second_name', label: 'Apellido materno' },
      { key: 'user_rh.email', label: 'Email' },
      { key: 'user_rh.external_user', label: 'Externo', formatter: isExternalUser },
      { key: 'user_rh.additional_info.unit', label: 'Sucursal', formatter: resolveEmployeeFacility },
      { key: 'belongs_to_crisis', label: 'Evaluado'},
      { key: 'actions', label: 'Acciones', thStyle: { width: '50px' } },
    ];

    const isSortDirDesc = ref(true);
    const perPage = ref(5);
    const refListTable = ref(null);
    const search = ref("");
    const showModal = ref(false);
    const sortBy = ref("created_at");
    const totalList = ref(0);

    //COMPUTED
    const dataMeta = computed(() => {
      const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
      return {
        from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalList.value,
      };
    });

    //METHODS
    const belongsToCrisis = (user) => {
      return (user.call_tree_status && user.contacted_by);
    }

    const fetchList = debounce(function(ctx, callback) {
      if (!props.isActive) {
        callback([]);
        return;
      }

      const params = {
        q: search.value,
        max: perPage.value,
        'page[n]': currentPage.value,
        id_crisis: props.operationalSecurityCrisis.id,
        id_unit: props.branch.id_unit,
        withCrisisRelations: true
      }

      axios.get('v1/operational-security-crisis-units-users-rh', { params } )
        .then(response => {
          callback(response.data.data)
          totalList.value = response.data.meta.total
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error obteniendo la información',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        });
    }, 200)

    const handleSaveAdditionalEmployee = (data) => {
      emit('save-employee-additional-success', data);
      refetchData();
    }

    const refetchData = () => {
      if (refListTable.value) {
        refListTable.value.refresh();
      }
    };

    const selectEmployee = (employee) => {
      emit('select-employee', employee);
    };

    const update = (data) => {
      currentPage.value = data;
    }

    //WATCHERS
    watch([currentPage, perPage, sortBy, isSortDirDesc], () => { refetchData(); });
    watch([search], debounce(() => { refetchData(); }, 500));
    watch(() => props.isActive, (active) => {
      if (active) {
        refetchData();
      }
    });

    return {
      tableWrapper,

      currentPage,
      employeesColumns,
      isSortDirDesc,
      perPage,
      refListTable,
      search,
      showModal,
      sortBy,
      totalList,

      dataMeta,

      belongsToCrisis,
      fetchList,
      handleSaveAdditionalEmployee,
      refetchData,
      selectEmployee,
      update
    }
  }
}
</script>

<style lang="scss" scoped>
.toolbar-gap {
  gap: 8px;
}

.search-field {
  max-width: 260px;
}
</style>
